import type { DmPatient } from '../../../../../services/dmPatient';
import {
    formatYmd,
    isoToYmd,
    monitoringState,
    todayYmd,
    type MonitoringState
} from './dmControlUtils.ts';

// The schedule and aligner settings, edited as one draft and saved by the footer. The monitoring
// status is not part of it: those actions apply at once (see MonitoringActions).

export interface DmBaseline {
    status: MonitoringState;
    resumeOn: string | null;
    nextScan: string | null;
    interval: number | null;
    /** Dynamic aligner change protocols also carry a follow-up delay after a NO-GO. */
    needsNoGo: boolean;
    noGo: number | null;
    current: number | null;
    total: number | null;
    /** Off for fixed appliances: the aligner fields are neither shown, checked nor sent. */
    tracksAligners: boolean;
}

export interface DmDraft {
    nextScan: string;
    interval: string;
    noGo: string;
    current: string;
    total: string;
}

export type DraftPatch = Partial<DmDraft>;

export const MAX_INTERVAL = 90;
export const MAX_ALIGNER = 200;

export function baselineOf(patient: DmPatient): DmBaseline {
    return {
        status: monitoringState(patient.status),
        resumeOn: isoToYmd(patient.resumeDate),
        nextScan: isoToYmd(patient.nextScanDate),
        interval: patient.intervalDays ?? null,
        needsNoGo: patient.dynamicAlignerChange === true,
        noGo: patient.requestControlScanAfterDays ?? null,
        current: patient.currentAligner ?? null,
        total: patient.totalAligners ?? null,
        tracksAligners: patient.tracksAligners !== false
    };
}

export function initialDraft(base: DmBaseline): DmDraft {
    return {
        nextScan: base.nextScan ?? '',
        interval: String(base.interval ?? ''),
        noGo: String(base.noGo ?? ''),
        current: String(base.current ?? 1),
        total: String(base.total ?? 1)
    };
}

/** Settings DM accepts only while monitoring runs (active or paused). */
export function settingsEditable(base: DmBaseline): boolean {
    return base.status === 'active' || base.status === 'paused';
}

/** DM moves the next scan only while monitoring is active; paused, it follows the resume date. */
export function nextScanEditable(base: DmBaseline): boolean {
    return base.status === 'active';
}

export function nextScanChanged(base: DmBaseline, draft: DmDraft): boolean {
    return nextScanEditable(base) && draft.nextScan !== (base.nextScan ?? '');
}

export interface TouchedSettings {
    /** Interval and NO-GO go to DM together, so either one sends both. */
    schedule: boolean;
    aligner: boolean;
}

// Compared with what the form started from, not with DM: a setting DM has no value for starts
// from a default, and leaving that default alone is not a change.
export function touchedSettings(base: DmBaseline, draft: DmDraft): TouchedSettings {
    const initial = initialDraft(base);
    const moved = (key: keyof DmDraft) => Number(draft[key]) !== Number(initial[key]);
    return {
        schedule: moved('interval') || (base.needsNoGo && moved('noGo')),
        aligner: base.tracksAligners && (moved('current') || moved('total'))
    };
}

export interface DraftChange {
    key: 'next-scan' | 'interval' | 'no-go' | 'aligner';
    label: string;
}

export function draftChanges(base: DmBaseline, draft: DmDraft): DraftChange[] {
    if (!settingsEditable(base)) return [];
    const changes: DraftChange[] = [];
    if (nextScanChanged(base, draft)) {
        changes.push({ key: 'next-scan', label: `Next scan ${formatYmd(draft.nextScan)}` });
    }
    const initial = initialDraft(base);
    const touched = touchedSettings(base, draft);
    if (touched.schedule && Number(draft.interval) !== Number(initial.interval)) {
        changes.push({ key: 'interval', label: `Every ${draft.interval} days` });
    }
    if (touched.schedule && base.needsNoGo && Number(draft.noGo) !== Number(initial.noGo)) {
        changes.push({ key: 'no-go', label: `NO-GO after ${draft.noGo} days` });
    }
    if (touched.aligner) {
        changes.push({ key: 'aligner', label: `Aligner #${draft.current} of ${draft.total}` });
    }
    return changes;
}

export interface DraftErrors {
    nextScan?: string;
    interval?: string;
    noGo?: string;
    aligner?: string;
}

function isWhole(value: number, min: number, max: number): boolean {
    return Number.isInteger(value) && value >= min && value <= max;
}

// Only settings that will be sent are checked, so a value DM lacks never blocks the others.
function scheduleErrors(base: DmBaseline, draft: DmDraft): DraftErrors {
    const interval = Number(draft.interval);
    const noGo = Number(draft.noGo);
    if (!isWhole(interval, 1, MAX_INTERVAL)) {
        return { interval: `The interval must be 1 to ${MAX_INTERVAL} days.` };
    }
    if (base.needsNoGo && !(isWhole(noGo, 1, MAX_INTERVAL - 1) && noGo < interval)) {
        return { noGo: 'The NO-GO scan must come sooner than the regular interval.' };
    }
    return {};
}

function alignerErrors(draft: DmDraft): DraftErrors {
    const current = Number(draft.current);
    const total = Number(draft.total);
    if (!isWhole(current, 1, MAX_ALIGNER) || !isWhole(total, 1, MAX_ALIGNER)) {
        return { aligner: `Aligner numbers must be 1 to ${MAX_ALIGNER}.` };
    }
    return current > total ? { aligner: "Current can't be higher than total." } : {};
}

export function draftErrors(base: DmBaseline, draft: DmDraft): DraftErrors {
    if (!settingsEditable(base)) return {};
    const errors: DraftErrors = {};
    if (nextScanChanged(base, draft) && (!draft.nextScan || draft.nextScan < todayYmd())) {
        errors.nextScan = 'Pick today or a later date.';
    }
    const touched = touchedSettings(base, draft);
    return {
        ...errors,
        ...(touched.schedule ? scheduleErrors(base, draft) : {}),
        ...(touched.aligner ? alignerErrors(draft) : {})
    };
}
